import { inject } from '@angular/core';
import { Route } from '@angular/router';
import { EmptyRouterOutletComponent } from '@rxap/components';
import { folderShareSupportedGuard } from './shared/guards/folder-share-supported.guard';
import { ingestGuard } from './shared/guards/ingest-guard';
import { ipfsSourceReadyGuard } from './shared/guards/ipfs-ready.guard';
import { localizationGuard } from './shared/guards/localization.guard';
import { productsGuard } from './shared/guards/products.guard';
import { requestApiEnabledGuard } from './shared/guards/request-api-enabled.guard';
import { setLanguageGuard } from './shared/guards/set-language.guard';
import { uploadDefaultGuard } from './shared/guards/upload-default.guard';
import { uploadEnabledGuard } from './shared/guards/upload-enabled.guard';
import { validLanguageGuard } from './shared/guards/valid-language.guard';
import { SearchService } from './shared/search/search.service';

export const appRoutes: Route[] = [
  {
    path: 'not-found',
    loadComponent: () =>
      import('./shared/components/not-found/not-found.component'),
  },
  {
    path: 'processing',
    loadComponent: () =>
      import('./shared/components/processing/processing.component'),
  },
  {
    path: 'ipfs-connecting',
    loadComponent: () =>
      import('./shared/components/ipfs-connecting/ipfs-connecting.component'),
  },
  {
    path: 'invalid-config',
    loadComponent: () =>
      import('./shared/components/invalid-config/invalid-config.component'),
  },
  {
    path: 'invalid-iirds',
    loadComponent: () =>
      import('./shared/components/invalid-iirds/invalid-iirds.component'),
  },
  {
    path: 'upload',
    canActivate: [uploadEnabledGuard],
    loadComponent: () =>
      import(
        './shared/components/iirds-explorer/explorer-shell/explorer-shell.component'
      ).then((m) => m.ExplorerShellComponent),
    children: [
      {
        path: '',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './shared/components/iirds-explorer/source-selection/source-selection.component'
          ).then((m) => m.SourceSelectionComponent),
      },
      {
        path: 'files',
        loadComponent: () =>
          import(
            './shared/components/iirds-explorer/file-upload-source/file-upload-source.component'
          ).then((m) => m.FileUploadSourceComponent),
      },
      {
        path: 'folder',
        canActivate: [folderShareSupportedGuard],
        loadComponent: () =>
          import(
            './shared/components/iirds-explorer/folder-source/folder-source.component'
          ).then((m) => m.FolderSourceComponent),
      },
      {
        path: 'request-api',
        canActivate: [requestApiEnabledGuard],
        loadComponent: () =>
          import(
            './shared/components/iirds-explorer/request-api-source/request-api-source.component'
          ).then((m) => m.RequestApiSourceComponent),
      },
      {
        path: 'cid',
        canActivate: [ipfsSourceReadyGuard],
        loadComponent: () =>
          import(
            './shared/components/iirds-explorer/cid-source/cid-source.component'
          ).then((m) => m.CidSourceComponent),
      },
      { path: '**', redirectTo: '' },
    ],
  },
  {
    path: 'imprint',
    loadComponent: () => import('./shared/components/legal/legal.component'),
    data: {
      src: 'assets/legal/imprint.md',
    },
  },
  {
    path: 'privacy',
    loadComponent: () => import('./shared/components/legal/legal.component'),
    data: {
      src: 'assets/legal/privacy.md',
    },
  },
  {
    path: 'iirds',
    component: EmptyRouterOutletComponent,
    canActivate: [uploadDefaultGuard, productsGuard],
    children: [
      {
        path: ':language',
        loadComponent: () =>
          import('./components/help-overview/help-overview.component'),
        canActivate: [validLanguageGuard, setLanguageGuard],
        resolve: {
          ready: () => inject(SearchService).checkReady(),
        },
      },
      {
        path: '',
        loadComponent: () =>
          import(
            './shared/components/localization-selector/localization-selector.component'
          ),
        canActivate: [localizationGuard],
      },
    ],
  },
  {
    path: 'search',
    canActivate: [ingestGuard, setLanguageGuard],
    loadChildren: () => import('./layout.routes'),
    resolve: {
      ready: () => inject(SearchService).checkReady(),
    },
  },
  {
    path: '**',
    redirectTo: 'iirds',
  },
];

export default appRoutes;
